import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../resources/AuthProvider";
import {
  Button,
  Checkbox,
  CircularProgress,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  TextField,
  useMediaQuery,
} from "@mui/material";
import {
  AccountCircleOutlined,
  AdminPanelSettingsOutlined,
  CancelOutlined,
  CalendarMonthOutlined,
  CheckCircleOutline,
  CheckOutlined,
  CloseOutlined,
  DeleteOutline,
  DownloadOutlined,
  EditOutlined,
  FilterAltOutlined,
  KeyboardArrowLeftOutlined,
  KeyboardArrowRightOutlined,
  KeyboardDoubleArrowLeftOutlined,
  KeyboardDoubleArrowRightOutlined,
  LockResetOutlined,
  MoreVertOutlined,
  PeopleAltOutlined,
  PersonOffOutlined,
  SearchOutlined,
  SupervisorAccountOutlined,
  VisibilityOutlined,
} from "@mui/icons-material";

import SelectField from "components/atoms/SelectField/SelectField";
import ConfirmDialog from "components/atoms/ConfirmDialog/ConfirmDialog";
import MultiActionButton from "components/atoms/MultiActionButton/MultiActionButton";
import {
  activateUser,
  activateUsers,
  countUsuarios,
  deleteUser,
  fetchUsuarios,
  fetchUsuariosTotalizadores,
  findUsuarioInscricoes,
  inactivateUser,
  inactivateUsers,
  newpasswordUsers,
  sendTemporaryPassword,
  UsuariosTotalizadores,
  UsuarioInscricao,
} from "API/fetchUsuarios";
import { BaseUsuario } from "types/Models";
import { ExportColumn } from "types/ExportTypes";
import { formatarCpf } from "utils/Masks";
import { exportPDF } from "utils/exporters/exportPDF";
import { exportXLSX } from "utils/exporters/exportXLSX";

import stylesButton from "styles/Button.module.css";
import stylesInput from "styles/Input.module.css";
import stylesManagement from "styles/ManagementList.module.css";
import styles from "./UserAdminPage.module.css";

type OrderBy =
  | "nome"
  | "data_cadastro"
  | "ultimo_acesso"
  | "perfil"
  | "municipio"
  | "status";
type OrderDirection = "asc" | "desc";

interface UserFilters {
  search: string;
  userType: string;
  status?: number;
  ambito?: number;
  municipio?: number;
  lotacao?: number;
  accessFilter: string;
  orderBy: OrderBy;
  orderDirection: OrderDirection;
}

const emptyTotals: UsuariosTotalizadores = {
  usuarios_cadastrados: 0,
  usuarios_ativos: 0,
  usuarios_inativos: 0,
  responsaveis_tecnicos: 0,
  usuarios: 0,
  colaboradores_externos: 0,
  administradores: 0,
  ambito_estadual: 0,
  ambito_municipal: 0,
  acessos_hoje: 0,
  acessos_ultimos_7_dias: 0,
  acessos_ultimos_30_dias: 0,
  nunca_acessou: 0,
};

const initialFilters: UserFilters = {
  search: "",
  userType: "",
  status: undefined,
  ambito: undefined,
  municipio: undefined,
  lotacao: undefined,
  accessFilter: "",
  orderBy: "nome",
  orderDirection: "asc",
};

const userTypeMap: Record<string, string> = {
  Administrador: "Administrador",
  "Responsável Técnico": "Responsável Técnico",
  "Colaborador Externo": "Parceiros",
  Usuário: "Usuário",
};

const statusOptions = [
  { id: 1, nome: "Ativo" },
  { id: 2, nome: "Inativo" },
];

const orderOptions = [
  {
    value: "mais_recentes",
    label: "Mais recentes",
    orderBy: "data_cadastro",
    orderDirection: "desc",
  },
  {
    value: "mais_antigas",
    label: "Mais antigas",
    orderBy: "data_cadastro",
    orderDirection: "asc",
  },
  {
    value: "nome_az",
    label: "Nome (A-Z)",
    orderBy: "nome",
    orderDirection: "asc",
  },
  {
    value: "nome_za",
    label: "Nome (Z-A)",
    orderBy: "nome",
    orderDirection: "desc",
  },
  {
    value: "municipio",
    label: "Município",
    orderBy: "municipio",
    orderDirection: "asc",
  },
  {
    value: "status",
    label: "Status",
    orderBy: "status",
    orderDirection: "asc",
  },
  {
    value: "perfil",
    label: "Perfil",
    orderBy: "perfil",
    orderDirection: "asc",
  },
  {
    value: "ultimo_acesso",
    label: "Último acesso",
    orderBy: "ultimo_acesso",
    orderDirection: "desc",
  },
] as const satisfies ReadonlyArray<{
  value: string;
  label: string;
  orderBy: OrderBy;
  orderDirection: OrderDirection;
}>;

type UserOrderOption = (typeof orderOptions)[number]["value"];

const formatLastAccess = (value?: string | null) => {
  if (!value) return "Nunca acessou";

  const accessDate = new Date(value);
  if (Number.isNaN(accessDate.getTime())) return "-";

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const accessDay = new Date(
    accessDate.getFullYear(),
    accessDate.getMonth(),
    accessDate.getDate(),
  );
  const days = Math.round((today.getTime() - accessDay.getTime()) / 86_400_000);
  const time = accessDate.toLocaleTimeString("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
  });

  if (days === 0) return `Hoje ${time}`;
  if (days === 1) return "Ontem";
  return `há ${days} dias`;
};

const avatarColors = [
  styles.avatarBlue,
  styles.avatarGreen,
  styles.avatarPurple,
  styles.avatarAmber,
  styles.avatarTeal,
  styles.avatarRose,
];

const getAvatarColor = (name: string) => {
  const value = Array.from(name).reduce(
    (total, character) => total + character.charCodeAt(0),
    0,
  );
  return avatarColors[value % avatarColors.length];
};

const getUserDisplayName = (user: BaseUsuario) => {
  const nomeSocial = user.nome_social?.trim();
  return user.possui_nome_social && nomeSocial
    ? `${user.nome} / ${nomeSocial}`
    : user.nome;
};

const getRoleBadgeColor = (role?: string | null) => {
  const normalizedRole = (role ?? "").toLocaleLowerCase("pt-BR");
  if (normalizedRole.includes("administr")) return styles.roleBadgeAdmin;
  if (normalizedRole.includes("respons")) return styles.roleBadgeTechnical;
  if (
    normalizedRole.includes("colaborador") ||
    normalizedRole.includes("parceiro")
  )
    return styles.roleBadgeExternal;
  return styles.roleBadgeUser;
};

const UserAdminPageSkeleton = () => (
  <div
    className={styles.page}
    aria-busy="true"
    aria-label="Carregando administração de usuários"
  >
    <header className={styles.pageHeader}>
      <Skeleton variant="text" width={310} height={40} />
    </header>
    <section className={styles.indicatorsGrid}>
      {Array.from({ length: 6 }, (_, index) => (
        <div key={index} className={styles.indicatorCard}>
          <Skeleton variant="circular" width={48} height={48} />
          <div>
            <Skeleton variant="text" width="75%" />
            <Skeleton variant="text" width={76} height={42} />
            <Skeleton variant="text" width={58} />
          </div>
        </div>
      ))}
    </section>
    <section className={styles.searchBar}>
      <Skeleton
        variant="rounded"
        height={40}
        className={styles.skeletonSearch}
      />
      <Skeleton variant="rounded" width={162} height={40} />
    </section>
    <section className={stylesManagement.workspace}>
      <aside className={stylesManagement.refinePanel}>
        <Skeleton variant="text" width={150} height={26} />
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className={stylesManagement.refineGroup}>
            <Skeleton variant="text" width={95} />
            {Array.from({ length: index === 3 ? 4 : 3 }, (_, row) => (
              <Skeleton
                key={row}
                variant="text"
                width={row % 2 ? "70%" : "88%"}
                height={28}
              />
            ))}
          </div>
        ))}
      </aside>
      <main className={stylesManagement.resultsPanel}>
        <div className={stylesManagement.resultsHeader}>
          <Skeleton variant="rounded" width={205} height={38} />
          <Skeleton variant="rounded" width={235} height={36} />
        </div>
        <div className={stylesManagement.tableContainer}>
          {Array.from({ length: 9 }, (_, index) => (
            <Skeleton
              key={index}
              variant="rectangular"
              height={43}
              className={styles.tableSkeletonRow}
            />
          ))}
        </div>
        <div className={stylesManagement.pagination}>
          <Skeleton variant="rounded" width={165} height={34} />
          <Skeleton variant="rounded" width={205} height={34} />
        </div>
      </main>
    </section>
  </div>
);

const UserAdminPage = () => {
  const { user, hasActionPermission } = useAuth();
  const navigate = useNavigate();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [usuarios, setUsuarios] = useState<BaseUsuario[]>([]);
  const [totais, setTotais] = useState<UsuariosTotalizadores>(emptyTotals);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [perPage, setPerPage] = useState(10);
  const [loading, setLoading] = useState(false);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  const [loadingExport, setLoadingExport] = useState(false);
  const [filters, setFilters] = useState<UserFilters>(initialFilters);
  const [appliedFilters, setAppliedFilters] =
    useState<UserFilters>(initialFilters);
  const filtersOpen = true;
  const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());
  const [selectedUser, setSelectedUser] = useState<BaseUsuario | null>(null);
  const [detailUser, setDetailUser] = useState<BaseUsuario | null>(null);
  const [detailTab, setDetailTab] = useState(0);
  const [inscricoesUsuario, setInscricoesUsuario] = useState<
    UsuarioInscricao[]
  >([]);
  const [loadingInscricoesUsuario, setLoadingInscricoesUsuario] =
    useState(false);
  const canViewUserInscricoes =
    hasActionPermission("usuarios.view_all") ||
    hasActionPermission("usuarios.view_own");
  const [actionAnchor, setActionAnchor] = useState<HTMLElement | null>(null);
  const [exportAnchor, setExportAnchor] = useState<HTMLElement | null>(null);
  const [msgSuccess, setMsgSuccess] = useState<string | null>(null);
  const [msgError, setMsgError] = useState<string | null>(null);
  const [loadingDialog, setLoadingDialog] = useState(false);
  const [dialog, setDialog] = useState<
    | "password"
    | "activate"
    | "inactivate"
    | "delete"
    | "bulkPassword"
    | "bulkActivate"
    | "bulkInactivate"
    | "bulkDelete"
    | null
  >(null);

  useEffect(() => {
    const carregarInscricoes = async () => {
      if (!detailUser || detailTab !== 1 || !canViewUserInscricoes) return;

      try {
        setLoadingInscricoesUsuario(true);
        setInscricoesUsuario(await findUsuarioInscricoes(detailUser.id));
      } catch {
        setInscricoesUsuario([]);
      } finally {
        setLoadingInscricoesUsuario(false);
      }
    };

    void carregarInscricoes();
  }, [canViewUserInscricoes, detailTab, detailUser]);

  const requestArguments = useMemo(
    () =>
      [
        undefined,
        appliedFilters.userType
          ? userTypeMap[appliedFilters.userType] || ""
          : "",
        appliedFilters.status,
        appliedFilters.ambito,
        appliedFilters.municipio,
        appliedFilters.lotacao,
        undefined,
        undefined,
        undefined,
        appliedFilters.search,
        appliedFilters.accessFilter,
        appliedFilters.orderBy,
        appliedFilters.orderDirection,
      ] as const,
    [appliedFilters],
  );

  const loadUsers = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const [data, total, totalizadores] = await Promise.all([
        fetchUsuarios(currentPage, perPage, user, ...requestArguments),
        countUsuarios(user, ...requestArguments),
        fetchUsuariosTotalizadores(user, ...requestArguments),
      ]);
      setUsuarios(data);
      setTotalUsers(total);
      setTotalPages(Math.max(1, Math.ceil(total / perPage)));
      setTotais({ ...emptyTotals, ...totalizadores });
    } catch (error) {
      setMsgError(
        error instanceof Error
          ? error.message
          : "Não foi possível carregar os usuários.",
      );
    } finally {
      setLoading(false);
      setHasLoadedInitialData(true);
    }
  }, [currentPage, perPage, requestArguments, user]);

  useEffect(() => {
    void loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    if (!msgSuccess && !msgError) return;
    const timer = window.setTimeout(() => {
      setMsgSuccess(null);
      setMsgError(null);
    }, 5000);
    return () => window.clearTimeout(timer);
  }, [msgError, msgSuccess]);

  const applyFilters = (nextFilters = filters) => {
    setCurrentPage(1);
    setSelectedUsers(new Set());
    setAppliedFilters({ ...nextFilters });
  };

  const updateAndApplyFilter = (changes: Partial<UserFilters>) => {
    const next = { ...filters, ...changes };
    setFilters(next);
    applyFilters(next);
  };

  const clearFilters = () => {
    setFilters(initialFilters);
    applyFilters(initialFilters);
  };

  const handleUserSelection = (id: string | number) => {
    const selectedId = String(id);
    setSelectedUsers((current) => {
      const next = new Set(current);
      next.has(selectedId) ? next.delete(selectedId) : next.add(selectedId);
      return next;
    });
  };

  const allUsersSelected =
    usuarios.length > 0 &&
    usuarios.every((item) => selectedUsers.has(String(item.id)));
  const someUsersSelected = usuarios.some((item) =>
    selectedUsers.has(String(item.id)),
  );
  const pageLoading = loading || !hasLoadedInitialData;

  const handleSelectAllUsers = () => {
    setSelectedUsers((current) => {
      const next = new Set(current);
      usuarios.forEach((item) =>
        allUsersSelected
          ? next.delete(String(item.id))
          : next.add(String(item.id)),
      );
      return next;
    });
  };

  const openActionMenu = (
    event: React.MouseEvent<HTMLElement>,
    target: BaseUsuario,
  ) => {
    event.stopPropagation();
    setSelectedUser(target);
    setActionAnchor(event.currentTarget);
  };

  const closeActionMenu = () => setActionAnchor(null);
  const openDialog = (nextDialog: NonNullable<typeof dialog>) => {
    closeActionMenu();
    setDialog(nextDialog);
  };

  const executeDialog = async () => {
    try {
      setLoadingDialog(true);
      const ids = Array.from(selectedUsers).map(Number);
      switch (dialog) {
        case "password":
          if (selectedUser) await sendTemporaryPassword(selectedUser.id);
          setMsgSuccess("Senha temporária enviada com sucesso!");
          break;
        case "activate":
          if (selectedUser) await activateUser(selectedUser.id);
          setMsgSuccess("Usuário reativado com sucesso!");
          break;
        case "inactivate":
          if (selectedUser) await inactivateUser(selectedUser.id);
          setMsgSuccess("Usuário inativado com sucesso!");
          break;
        case "delete":
          if (selectedUser) await deleteUser(selectedUser.id);
          setMsgSuccess("Usuário excluído com sucesso!");
          break;
        case "bulkPassword":
          await newpasswordUsers(ids);
          setMsgSuccess("Senhas temporárias enviadas com sucesso!");
          break;
        case "bulkActivate":
          await activateUsers(ids);
          setMsgSuccess("Usuários reativados com sucesso!");
          break;
        case "bulkInactivate":
          await inactivateUsers(ids);
          setMsgSuccess("Usuários inativados com sucesso!");
          break;
        case "bulkDelete":
          for (const id of ids) await deleteUser(id);
          setMsgSuccess("Usuários excluídos com sucesso!");
          break;
      }
      setDialog(null);
      setSelectedUsers(new Set());
      await loadUsers();
    } catch (error) {
      setDialog(null);
      setMsgError(
        error instanceof Error
          ? error.message
          : "Não foi possível concluir a operação.",
      );
    } finally {
      setLoadingDialog(false);
    }
  };

  const exportColumns: ExportColumn[] = [
    { label: "Nome", key: "nome" },
    { label: "CPF", key: "cpf" },
    { label: "E-mail", key: "email" },
    { label: "Âmbito", key: "ambito" },
    { label: "Lotação", key: "lotacao" },
    { label: "Município", key: "municipio" },
    { label: "Perfil", key: "role" },
    { label: "Status", key: "ativo" },
  ];

  const handleExport = async (format: "xlsx" | "pdf" | "csv") => {
    if (!user) return;
    setExportAnchor(null);
    setLoadingExport(true);
    try {
      const data = await fetchUsuarios(1, 999999, user, ...requestArguments);
      if (!data.length)
        throw new Error("Não existem dados para serem exportados.");
      const rows: Record<string, unknown>[] = data.map((item) => ({
        ...item,
        cpf: formatarCpf(item.cpf),
        ativo: item.ativo ? "Ativo" : "Inativo",
      }));
      if (format === "xlsx") exportXLSX(exportColumns, rows, "usuarios.xlsx");
      if (format === "pdf") exportPDF(exportColumns, rows, "usuarios.pdf");
      if (format === "csv") {
        const csv = [
          exportColumns.map((column) => column.label).join(";"),
          ...rows.map((row) =>
            exportColumns
              .map(
                (column) =>
                  `"${String(row[column.key] ?? "").replace(/"/g, '""')}"`,
              )
              .join(";"),
          ),
        ].join("\n");
        const url = URL.createObjectURL(
          new Blob([csv], { type: "text/csv;charset=utf-8" }),
        );
        const link = document.createElement("a");
        link.href = url;
        link.download = "usuarios.csv";
        link.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      setMsgError(
        error instanceof Error
          ? error.message
          : "Não foi possível exportar os usuários.",
      );
    } finally {
      setLoadingExport(false);
    }
  };

  const indicatorCards = [
    {
      key: "usuarios_cadastrados",
      label: "Usuários cadastrados",
      icon: <PeopleAltOutlined />,
      className: styles.indicatorBlue,
      patch: {},
    },
    {
      key: "usuarios_ativos",
      label: "Usuários ativos",
      icon: <CheckCircleOutline />,
      className: styles.indicatorGreen,
      patch: { status: 1 },
    },
    {
      key: "usuarios_inativos",
      label: "Usuários inativos",
      icon: <PersonOffOutlined />,
      className: styles.indicatorGray,
      patch: { status: 2 },
    },
    {
      key: "responsaveis_tecnicos",
      label: "Responsáveis técnicos",
      icon: <SupervisorAccountOutlined />,
      className: styles.indicatorIndigo,
      patch: { userType: "Responsável Técnico" },
    },
    {
      key: "colaboradores_externos",
      label: "Colaboradores externos",
      icon: <AccountCircleOutlined />,
      className: styles.indicatorPurple,
      patch: { userType: "Colaborador Externo" },
    },
    {
      key: "administradores",
      label: "Administradores",
      icon: <AdminPanelSettingsOutlined />,
      className: styles.indicatorAmber,
      patch: { userType: "Administrador" },
    },
  ] as const;

  const visibleStart = totalUsers === 0 ? 0 : (currentPage - 1) * perPage + 1;
  const visibleEnd = Math.min(currentPage * perPage, totalUsers);
  const dialogTitle = {
    password: "Enviar nova senha",
    activate: "Reativar usuário",
    inactivate: "Inativar usuário",
    delete: "Excluir usuário",
    bulkPassword: "Reenviar senhas",
    bulkActivate: "Reativar usuários",
    bulkInactivate: "Inativar usuários",
    bulkDelete: "Excluir usuários",
  }[dialog || "password"];

  if (pageLoading) return <UserAdminPageSkeleton />;

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <h1>Administração de Usuários</h1>
      </header>
      <section className={styles.indicatorsGrid}>
        {indicatorCards.map((card) => (
          <button
            key={card.key}
            type="button"
            className={styles.indicatorCard}
            onClick={() => updateAndApplyFilter(card.patch)}
          >
            <div className={`${styles.indicatorIcon} ${card.className}`}>
              {card.icon}
            </div>
            <div>
              <span>{card.label}</span>
              <strong>{totais[card.key].toLocaleString("pt-BR")}</strong>
              <small>Ver todos</small>
            </div>
          </button>
        ))}
      </section>
      <section className={styles.searchBar}>
        <TextField
          fullWidth
          size="small"
          variant="standard"
          placeholder="Pesquisar por nome, CPF, e-mail, município ou lotação"
          value={filters.search}
          onChange={(event) =>
            setFilters((current) => ({
              ...current,
              search: event.target.value,
            }))
          }
          onKeyDown={(event) => event.key === "Enter" && applyFilters()}
          InputProps={{
            startAdornment: <SearchOutlined className={styles.searchIcon} />,
            className: stylesInput.inputSoft,
            disableUnderline: true,
          }}
        />
        <Button
          variant="contained"
          className={stylesButton.buttonSoftPrimary}
          startIcon={<FilterAltOutlined />}
          onClick={() => applyFilters()}
        >
          Filtrar
        </Button>
      </section>
      {(msgSuccess || msgError) && (
        <div
          className={msgSuccess ? styles.feedbackSuccess : styles.feedbackError}
        >
          {msgSuccess || msgError}
        </div>
      )}
      <section className={stylesManagement.workspace}>
        {filtersOpen && (
          <aside className={stylesManagement.refinePanel}>
            <div className={stylesManagement.refineHeader}>
              <strong>Refinar resultados</strong>
              <button type="button" onClick={clearFilters}>
                Limpar filtros <DeleteOutline fontSize="inherit" />
              </button>
            </div>
            <div className={stylesManagement.refineGroup}>
              <span>Perfil de usuário</span>
              {[
                { id: "Administrador", total: totais.administradores },
                {
                  id: "Responsável Técnico",
                  total: totais.responsaveis_tecnicos,
                },
                {
                  id: "Colaborador Externo",
                  total: totais.colaboradores_externos,
                },
                { id: "Usuário", total: totais.usuarios },
              ].map((profile) => (
                <label
                  key={profile.id}
                  className={stylesManagement.refineOption}
                >
                  <span className={stylesManagement.refineOptionLabel}>
                    <Checkbox
                      size="small"
                      checked={filters.userType === profile.id}
                      onChange={() =>
                        updateAndApplyFilter({
                          userType:
                            filters.userType === profile.id ? "" : profile.id,
                        })
                      }
                    />
                    <span>{profile.id}</span>
                  </span>
                  <strong>{profile.total.toLocaleString("pt-BR")}</strong>
                </label>
              ))}
            </div>
            <div className={stylesManagement.refineGroup}>
              <span>Status</span>
              {statusOptions.map((status) => {
                const total =
                  status.id === 1
                    ? totais.usuarios_ativos
                    : totais.usuarios_inativos;
                return (
                  <label
                    key={status.id}
                    className={stylesManagement.refineOption}
                  >
                    <span className={stylesManagement.refineOptionLabel}>
                      <Checkbox
                        size="small"
                        checked={filters.status === status.id}
                        onChange={() =>
                          updateAndApplyFilter({
                            status:
                              filters.status === status.id
                                ? undefined
                                : status.id,
                          })
                        }
                      />
                      <i
                        className={
                          status.id === 1
                            ? styles.statusDotActive
                            : styles.statusDotInactive
                        }
                      />
                      <span>{status.nome}</span>
                    </span>
                    <strong>{total.toLocaleString("pt-BR")}</strong>
                  </label>
                );
              })}
            </div>
            <div className={stylesManagement.refineGroup}>
              <span>Âmbito</span>
              {[
                { id: 1, nome: "Estadual", total: totais.ambito_estadual },
                { id: 2, nome: "Municipal", total: totais.ambito_municipal },
              ].map((ambito) => (
                <label
                  key={ambito.id}
                  className={stylesManagement.refineOption}
                >
                  <span className={stylesManagement.refineOptionLabel}>
                    <Checkbox
                      size="small"
                      checked={filters.ambito === ambito.id}
                      onChange={() =>
                        updateAndApplyFilter({
                          ambito:
                            filters.ambito === ambito.id
                              ? undefined
                              : ambito.id,
                          municipio: undefined,
                          lotacao: undefined,
                        })
                      }
                    />
                    <span>{ambito.nome}</span>
                  </span>
                  <strong>{ambito.total.toLocaleString("pt-BR")}</strong>
                </label>
              ))}
            </div>
            <div className={stylesManagement.refineGroup}>
              <span>Último acesso</span>
              {[
                { id: "hoje", nome: "Hoje", total: totais.acessos_hoje },
                {
                  id: "ultimos_7_dias",
                  nome: "Últimos 7 dias",
                  total: totais.acessos_ultimos_7_dias,
                },
                {
                  id: "ultimos_30_dias",
                  nome: "Últimos 30 dias",
                  total: totais.acessos_ultimos_30_dias,
                },
                {
                  id: "nunca_acessou",
                  nome: "Nunca acessou",
                  total: totais.nunca_acessou,
                },
              ].map((access) => (
                <label
                  key={access.id}
                  className={stylesManagement.refineOption}
                >
                  <span className={stylesManagement.refineOptionLabel}>
                    <Checkbox
                      size="small"
                      checked={filters.accessFilter === access.id}
                      onChange={() =>
                        updateAndApplyFilter({
                          accessFilter:
                            filters.accessFilter === access.id ? "" : access.id,
                        })
                      }
                    />
                    <span>{access.nome}</span>
                  </span>
                  <strong>{access.total.toLocaleString("pt-BR")}</strong>
                </label>
              ))}
            </div>
            {/*
        <div className={stylesManagement.selectRefineGroup}><span>Município</span><SearchSelect label="" value={filters.municipio ?? null} onChange={(id) => updateAndApplyFilter({ municipio: id ?? undefined })} options={cidades} paperClassname={stylesInput.paper} popperClassname={stylesInput.popper} labelClassname={stylesInput.labelSelect} inputClassname={stylesInput.inputSelect} /></div>
        <div className={stylesManagement.selectRefineGroup}><span>Lotação</span><SearchSelect label="" value={filters.lotacao ?? null} onChange={(id) => updateAndApplyFilter({ lotacao: id ?? undefined })} options={lotacoes} paperClassname={stylesInput.paper} popperClassname={stylesInput.popper} labelClassname={stylesInput.labelSelect} inputClassname={stylesInput.inputSelect} /></div>
        */}
          </aside>
        )}
        <main className={stylesManagement.resultsPanel}>
          <div className={stylesManagement.resultsHeader}>
            <div className={stylesManagement.resultCount}>
              <CalendarMonthOutlined />
              <div>
                <strong>
                  {totalUsers.toLocaleString("pt-BR")} usuários encontrados
                </strong>
                <span>
                  Exibindo {visibleStart} a {visibleEnd} de {totalUsers}
                </span>
              </div>
            </div>
            <div className={stylesManagement.orderControl}>
              <label htmlFor="user-order">Ordenar por</label>
              <TextField
                id="user-order"
                select
                size="small"
                value={
                  orderOptions.find(
                    (option) =>
                      option.orderBy === filters.orderBy &&
                      option.orderDirection === filters.orderDirection,
                  )?.value ?? "nome_az"
                }
                onChange={(event) => {
                  const option = orderOptions.find(
                    (item) =>
                      item.value === (event.target.value as UserOrderOption),
                  );
                  if (option)
                    updateAndApplyFilter({
                      orderBy: option.orderBy,
                      orderDirection: option.orderDirection,
                    });
                }}
              >
                {orderOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
              <Button
                variant="outlined"
                className={stylesButton.buttonSoftOutline}
                startIcon={
                  loadingExport ? (
                    <CircularProgress size={15} color="inherit" />
                  ) : (
                    <DownloadOutlined />
                  )
                }
                onClick={(event) => setExportAnchor(event.currentTarget)}
                disabled={loadingExport}
              >
                Exportar
              </Button>
            </div>
          </div>
          {selectedUsers.size > 0 && (
            <div className={styles.bulkActions}>
              <span>{selectedUsers.size} selecionado(s)</span>
              <MultiActionButton
                buttonLabel="Ações em massa"
                actions={[
                  {
                    label: "Reenviar senha",
                    icon: <LockResetOutlined />,
                    onClick: () => setDialog("bulkPassword"),
                  },
                  {
                    label: "Inativar usuários",
                    icon: <CancelOutlined />,
                    onClick: () => setDialog("bulkInactivate"),
                  },
                  {
                    label: "Reativar usuários",
                    icon: <CheckOutlined />,
                    onClick: () => setDialog("bulkActivate"),
                  },
                  {
                    label: "Excluir usuários",
                    icon: <DeleteOutline />,
                    onClick: () => setDialog("bulkDelete"),
                  },
                ]}
              />
            </div>
          )}
          <TableContainer className={stylesManagement.tableContainer}>
            {loading ? (
              <div className={styles.loading}>
                <CircularProgress size={32} />
              </div>
            ) : (
              <Table size="small" className={stylesManagement.entityTable}>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={allUsersSelected}
                        indeterminate={!allUsersSelected && someUsersSelected}
                        onChange={handleSelectAllUsers}
                      />
                    </TableCell>
                    {isMobile ? (
                      <TableCell>Informações do usuário</TableCell>
                    ) : (
                      <>
                        <TableCell>Nome</TableCell>
                        <TableCell>CPF</TableCell>
                        <TableCell>Município</TableCell>
                        <TableCell>Lotação</TableCell>
                        <TableCell>Perfil</TableCell>
                        <TableCell>Último acesso</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell align="center">Ações</TableCell>
                      </>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {!usuarios.length && (
                    <TableRow>
                      <TableCell colSpan={isMobile ? 2 : 9} align="center">
                        Nenhum registro encontrado.
                      </TableCell>
                    </TableRow>
                  )}
                  {usuarios.map((item) => (
                    <TableRow
                      key={item.id}
                      hover
                      className={styles.userRow}
                      onClick={() => setDetailUser(item)}
                    >
                      <TableCell
                        padding="checkbox"
                        onClick={(event) => event.stopPropagation()}
                      >
                        <Checkbox
                          checked={selectedUsers.has(String(item.id))}
                          onChange={() => handleUserSelection(item.id)}
                        />
                      </TableCell>
                      {isMobile ? (
                        <TableCell>
                          <div className={styles.mobileUser}>
                            <strong>{getUserDisplayName(item)}</strong>
                            <span>{formatarCpf(item.cpf)}</span>
                            <span>{item.email}</span>
                            <span>
                              {item.municipio || "-"}{" "}
                              {item.lotacao ? `• ${item.lotacao}` : ""}
                            </span>
                            <div>
                              <span
                                className={`${styles.roleBadge} ${getRoleBadgeColor(item.role)}`}
                              >
                                {item.role}
                              </span>
                              <span
                                className={
                                  item.ativo
                                    ? styles.statusActive
                                    : styles.statusInactive
                                }
                              >
                                {item.ativo ? "Ativo" : "Inativo"}
                              </span>
                              <IconButton
                                size="small"
                                onClick={(event) => openActionMenu(event, item)}
                              >
                                <MoreVertOutlined fontSize="small" />
                              </IconButton>
                            </div>
                          </div>
                        </TableCell>
                      ) : (
                        <>
                          <TableCell>
                            <div className={styles.userName}>
                              <span
                                className={`${styles.userAvatar} ${getAvatarColor(item.nome)}`}
                              >
                                {item.nome
                                  .split(" ")
                                  .map((word) => word[0])
                                  .slice(0, 2)
                                  .join("")}
                              </span>
                              {getUserDisplayName(item)}
                            </div>
                          </TableCell>
                          <TableCell>{formatarCpf(item.cpf)}</TableCell>
                          <TableCell>{item.municipio || "-"}</TableCell>
                          <TableCell>{item.lotacao || "-"}</TableCell>
                          <TableCell>
                            <span
                              className={`${styles.roleBadge} ${getRoleBadgeColor(item.role)}`}
                            >
                              {item.role}
                            </span>
                          </TableCell>
                          <TableCell>
                            {formatLastAccess(item.last_login_at)}
                          </TableCell>
                          <TableCell>
                            <span
                              className={
                                item.ativo
                                  ? styles.statusActive
                                  : styles.statusInactive
                              }
                            >
                              {item.ativo ? "Ativo" : "Inativo"}
                            </span>
                          </TableCell>
                          <TableCell
                            align="center"
                            onClick={(event) => event.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              aria-label="Ações do usuário"
                              onClick={(event) => openActionMenu(event, item)}
                            >
                              <MoreVertOutlined fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </TableContainer>
          {usuarios.length > 0 && (
            <div className={stylesManagement.pagination}>
              <div className={styles.paginationSelect}>
                <span>Resultados por página</span>
                <SelectField
                  label=""
                  value={String(perPage)}
                  onChange={(value) => {
                    setCurrentPage(1);
                    setPerPage(Number(value));
                  }}
                  options={[{ nome: "10" }, { nome: "20" }, { nome: "30" }]}
                  inputClassName={stylesInput.inputSoft}
                  disableClearable
                  disableTyping
                />
              </div>
              <div className={styles.pageControls}>
                <IconButton
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(1)}
                >
                  <KeyboardDoubleArrowLeftOutlined />
                </IconButton>
                <IconButton
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((page) => page - 1)}
                >
                  <KeyboardArrowLeftOutlined />
                </IconButton>
                <strong>
                  Página {currentPage} de {totalPages}
                </strong>
                <IconButton
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((page) => page + 1)}
                >
                  <KeyboardArrowRightOutlined />
                </IconButton>
                <IconButton
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(totalPages)}
                >
                  <KeyboardDoubleArrowRightOutlined />
                </IconButton>
              </div>
              <div className={styles.paginationSelect}>
                <span>Ir para a página</span>
                <SelectField
                  label=""
                  value={String(currentPage)}
                  onChange={(value) => setCurrentPage(Number(value))}
                  options={Array.from({ length: totalPages }, (_, index) => ({
                    nome: String(index + 1),
                  }))}
                  inputClassName={stylesInput.inputSoft}
                  disableTyping
                />
              </div>
            </div>
          )}
        </main>
      </section>
      <Menu
        anchorEl={actionAnchor}
        open={Boolean(actionAnchor)}
        onClose={closeActionMenu}
      >
        <MenuItem
          onClick={() => {
            setDetailUser(selectedUser);
            closeActionMenu();
          }}
        >
          <VisibilityOutlined fontSize="small" />
          Visualizar
        </MenuItem>
        <MenuItem
          onClick={() =>
            selectedUser && navigate(`/user?cpf=${selectedUser.cpf}`)
          }
        >
          <EditOutlined fontSize="small" />
          Editar
        </MenuItem>
        {selectedUser?.ativo && (
          <MenuItem onClick={() => openDialog("password")}>
            <LockResetOutlined fontSize="small" />
            Resetar senha
          </MenuItem>
        )}
        <MenuItem
          onClick={() =>
            openDialog(selectedUser?.ativo ? "inactivate" : "activate")
          }
        >
          {selectedUser?.ativo ? (
            <PersonOffOutlined fontSize="small" />
          ) : (
            <CheckCircleOutline fontSize="small" />
          )}
          {selectedUser?.ativo ? "Inativar" : "Ativar"}
        </MenuItem>
        <MenuItem
          className={styles.deleteMenuItem}
          onClick={() => openDialog("delete")}
        >
          <DeleteOutline fontSize="small" />
          Excluir
        </MenuItem>
      </Menu>
      <Menu
        anchorEl={exportAnchor}
        open={Boolean(exportAnchor)}
        onClose={() => setExportAnchor(null)}
      >
        <MenuItem onClick={() => void handleExport("xlsx")}>
          Excel (.xlsx)
        </MenuItem>
        <MenuItem onClick={() => void handleExport("pdf")}>PDF (.pdf)</MenuItem>
        <MenuItem onClick={() => void handleExport("csv")}>CSV (.csv)</MenuItem>
      </Menu>
      <Drawer
        anchor="right"
        open={Boolean(detailUser)}
        onClose={() => setDetailUser(null)}
      >
        <div className={styles.detailDrawer}>
          {detailUser && (
            <>
              <div className={styles.drawerHeader}>
                <div>
                  <h2>{detailUser.nome}</h2>
                  <span>{formatarCpf(detailUser.cpf)}</span>
                </div>
                <IconButton onClick={() => setDetailUser(null)}>
                  <CloseOutlined />
                </IconButton>
              </div>
              <Tabs
                value={detailTab}
                onChange={(_, value) => setDetailTab(value)}
                className={styles.drawerTabs}
              >
                <Tab label="Dados cadastrais" value={0} />
                {canViewUserInscricoes && <Tab label="Inscrições" value={1} />}
              </Tabs>
              {detailTab === 0 && (
                <>
                  <div className={styles.drawerSection}>
                    <span
                      className={`${styles.roleBadge} ${getRoleBadgeColor(detailUser.role)}`}
                    >
                      {detailUser.role}
                    </span>
                    <span
                      className={
                        detailUser.ativo
                          ? styles.statusActive
                          : styles.statusInactive
                      }
                    >
                      {detailUser.ativo ? "Ativo" : "Inativo"}
                    </span>
                  </div>
                  <dl>
                    <div>
                      <dt>E-mail</dt>
                      <dd>{detailUser.email || "-"}</dd>
                    </div>
                    <div>
                      <dt>Município</dt>
                      <dd>{detailUser.municipio || "-"}</dd>
                    </div>
                    <div>
                      <dt>Lotação</dt>
                      <dd>{detailUser.lotacao || "-"}</dd>
                    </div>
                    <div>
                      <dt>Âmbito</dt>
                      <dd>{detailUser.ambito || "-"}</dd>
                    </div>
                    <div>
                      <dt>Último acesso</dt>
                      <dd>{formatLastAccess(detailUser.last_login_at)}</dd>
                    </div>
                    <div>
                      <dt>Data de cadastro</dt>
                      <dd>
                        {detailUser.created_at
                          ? new Date(detailUser.created_at).toLocaleDateString(
                              "pt-BR",
                            )
                          : "-"}
                      </dd>
                    </div>
                  </dl>
                  <Button
                    variant="contained"
                    className={stylesButton.buttonSoftPrimary}
                    startIcon={<EditOutlined />}
                    onClick={() => navigate(`/user?cpf=${detailUser.cpf}`)}
                  >
                    Editar usuário
                  </Button>
                </>
              )}
              {detailTab === 1 && canViewUserInscricoes && (
                <div className={styles.inscricoesList}>
                  {loadingInscricoesUsuario ? (
                    Array.from({ length: 4 }, (_, index) => (
                      <Skeleton key={index} variant="rounded" height={92} />
                    ))
                  ) : inscricoesUsuario.length === 0 ? (
                    <p>Nenhuma inscrição encontrada.</p>
                  ) : (
                    inscricoesUsuario.map((inscricao) => (
                      <article
                        key={inscricao.id}
                        className={styles.inscricaoItem}
                      >
                        <strong>{inscricao.acao?.nome || "-"}</strong>
                        <span>{inscricao.turma?.nome || "-"}</span>
                        <small>
                          {inscricao.turma?.descricao_modalidade || "-"} ·
                          Inscrito em{" "}
                          {inscricao.inscrito_em
                            ? new Date(
                                inscricao.inscrito_em,
                              ).toLocaleDateString("pt-BR")
                            : "-"}
                        </small>
                        <div>
                          <span
                            className={
                              inscricao.resultado?.status === "APROVADO"
                                ? styles.resultadoAprovado
                                : inscricao.resultado?.status === "REPROVADO"
                                  ? styles.resultadoReprovado
                                  : undefined
                            }
                          >
                            {inscricao.resultado?.status === "APROVADO"
                              ? "Aprovado"
                              : inscricao.resultado?.status === "REPROVADO"
                                ? "Reprovado"
                                : "Em andamento"}
                          </span>
                          {inscricao.resultado?.percentual_presenca != null && (
                            <span>
                              {inscricao.resultado.percentual_presenca}%
                              presença
                            </span>
                          )}
                        </div>
                      </article>
                    ))
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Drawer>
      <ConfirmDialog
        open={Boolean(dialog)}
        loading={loadingDialog}
        onClose={() => setDialog(null)}
        onConfirm={() => void executeDialog()}
        title={dialogTitle}
        message={
          dialog?.startsWith("bulk")
            ? "Deseja confirmar a operação para os usuários selecionados?"
            : `Deseja confirmar a operação para "${selectedUser?.nome || ""}"?`
        }
        titleLoadButton="Processando..."
        titleButtonConfirm="Confirmar"
      />
    </div>
  );
};

export default UserAdminPage;
